import type { SharingRequest } from '@ralph/contracts'
import type { I18n } from '@/i18n/index.ts'

/**
 * What a decision did, said after the card leaves the queue (PRD-26 GRP-080).
 *
 * The server may CLOSE a request instead of approving it — the person who asked no longer
 * maintains the item, it was paused, every group it named was deleted — and a card that simply
 * vanished would read as shared. So the outcome is read off what came back, never off what the
 * administrator pressed.
 *
 * The rule lives here rather than in `i18n/messages/`, because it is a rule (PRD-27 LOC-017,
 * D-25): a copy of it inside a locale's catalogue could drift there, and a `pt-BR` file that
 * lost the `closed` branch would tell Portuguese readers that nothing-was-shared was shared.
 * The catalogue is asked for the words through the parameter, so the language is always the one
 * the tab is rendering in (F-3).
 */
export const decisionOutcome = (
  fmt: Pick<I18n, 'locale' | 'formatCount' | 'formatList' | 't'>,
  request: Pick<SharingRequest, 'status' | 'closedReason' | 'resourceName' | 'requested' | 'requestedBy'>
): string => {
  const t = fmt.t.sharing
  if (request.status === 'approved') {
    return request.requested.kind === 'everyone'
      ? t.outcome.approvedEveryone(request.resourceName)
      : t.outcome.approvedGroups(
          fmt,
          request.requested.groups.map((group) => group.name),
          request.resourceName
        )
  }
  if (request.status === 'declined') {
    return t.outcome.declined(request.requestedBy?.displayName ?? t.personWhoAsked)
  }
  if (request.status === 'closed') {
    const why = request.closedReason ? t.closedReason[request.closedReason] : t.outcome.closedUnknown
    return t.outcome.closed(request.resourceName, why)
  }
  return t.outcome.other(t.status[request.status], request.resourceName)
}
